import { labelOf } from './dimensions';
import { postedAt } from './posted';
import type { MentionRow } from './generator';
import { mulberry32 } from './prng';
import { detectedSentiment, rowId, type RowId } from './sentimentOverrides';

/**
 * Page conversations — the posts behind one row of Competitive page
 * performance, for the modal a click on that row opens (2125:84169).
 *
 * A page is one brand on one channel (`data/pages.ts`), so its conversations
 * are exactly the mentions of that brand on that channel — which is what makes
 * the modal reconcile with the row that opened it (settled with the user
 * 2026-09-21): as many conversations as the row's Total posts, and columns that
 * sum to the row's own Reactions, Comments, Shares and Total engagement.
 */

export type ConversationRow = {
  id: RowId;
  /** The post's text — synthesized; see `postCopy`. */
  text: string;
  /**
   * The corpus channel key — `facebook`, `tiktok`, … — which the `Channel`
   * column paints as a platform mark. Added 2026-09-25 with that column; see
   * `ConversationsPanel`.
   */
  channel: string;
  engagement: number;
  /** The sentiment the dashboard counts — corrected, if Tune sentiment has been used. */
  sentiment: string;
  topic: string;
  /** Reactions **including likes**, the same fold `data/pages.ts` makes, so the two agree. */
  reactions: number;
  comments: number;
  shares: number;
  intent: string;
  /**
   * When the post went up, to the minute — see `postedAt`. Named `day` because
   * the column it fills is sorted and headed as one; it carries a time because
   * the cell prints one.
   */
  day: number;
};

/* ----------------------------------------------------------- post copy */

/** Separate from the corpus seed and from `postBreakdown`'s, so neither stream moves. */
const COPY_SEED = 0x51c0_7a3e;

/**
 * Post templates by sentiment. **Invented fixture copy** (settled with the user
 * 2026-09-21): the corpus holds no post text, and the frame's "WIN the ultimate
 * @elemis_uki giveaway…" is the component's example content.
 *
 * Two rules keep it honest:
 *
 * - **Keyed on the *detected* sentiment**, not the counted one. A post's words
 *   are a fact about the post; a Tune sentiment correction changes how the post
 *   is *counted* and must not rewrite what it said.
 * - **The topic always follows a preposition** ("about pricing", "on
 *   sponsorships"), never leads a verb. Topic labels include plurals and
 *   abstractions — Sponsorships, Careers, Sustainability — and this is the one
 *   phrasing that reads for every one of them. The breakdown drawer's comment
 *   templates learned the same lesson the hard way.
 */
const TEMPLATES: Readonly<Record<string, readonly string[]>> = {
  positive: [
    'Big thanks to {brand} for the quick help on {topic} 🙌',
    'Really impressed with how {brand} is handling {topic} lately.',
    'Exciting news from {brand} on {topic} this week 📱',
    "Credit where it's due, {brand} got it right on {topic} this time.",
  ],
  neutral: [
    'Has anyone seen the latest {brand} update on {topic}?',
    'Comparing operators on {topic} this week, will share what I find.',
    'Quick question for {brand} about {topic}. Does it cover prepaid users too?',
    'Heard {brand} is making changes to {topic} next month. Any details?',
  ],
  negative: [
    'Third day in a row of trouble with {topic}. Come on {brand} 😤',
    'Anyone else fed up with {brand} on {topic} lately?',
    'Called {brand} support twice about {topic}, still waiting.',
    'Seriously thinking of leaving {brand} over {topic}.',
  ],
};

/** A label mid-sentence: lowercase the first letter unless it opens an acronym (`SIM activation`). */
function midSentence(label: string): string {
  return label.length > 1 && label[1] === label[1].toLowerCase()
    ? label[0].toLowerCase() + label.slice(1)
    : label;
}

/** The post's text, the same on every render: one seeded draw per post. */
export function postCopy(row: MentionRow): string {
  const id = rowId(row);
  const rand = mulberry32((COPY_SEED ^ Math.imul(id + 1, 0x85eb_ca6b)) >>> 0);
  const pool = TEMPLATES[detectedSentiment(row)] ?? TEMPLATES.neutral;
  return pool[Math.floor(rand() * pool.length)]
    .replace('{topic}', midSentence(labelOf('topic', row.topic)))
    .replace('{brand}', labelOf('brand', row.brand));
}

/* ---------------------------------------------------------- the list */

/**
 * Corpus rows as conversations, in corpus order.
 *
 * Separated from `pageConversations` (2026-09-24) when a chart readout gained a
 * drill-down: a slice, a date or a heatmap cell picks a different subset of the
 * same corpus, and every one of them wants the same ten columns out of it. The
 * selecting is the caller's; this is only the shape.
 */
export function conversationsOf(rows: readonly MentionRow[]): ConversationRow[] {
  return rows.map((row) => ({
    id: rowId(row),
    text: postCopy(row),
    channel: row.channel,
    engagement: row.engagement,
    sentiment: row.sentiment,
    topic: labelOf('topic', row.topic),
    reactions: row.reactions + row.likes,
    comments: row.comments,
    shares: row.shares,
    intent: labelOf('intent', row.intent),
    day: postedAt(row),
  }));
}

/** The conversations of one page: that brand on that channel, in corpus order. */
export function pageConversations(
  rows: readonly MentionRow[],
  brand: string,
  channel: string,
): ConversationRow[] {
  return conversationsOf(rows.filter((row) => row.brand === brand && row.channel === channel));
}
